// @flow
import { useEffect, useRef } from 'react';
import useSaveSecondaryFormMutation from 'hooks/mutations/useSaveSecondaryFormMutation';
import useSavePrimaryFormMutation from 'hooks/mutations/useSavePrimaryFormMutation';
import { useUserContext } from 'contexts/UserContext';
import type { SectionsProgress } from 'hooks/sectionsProgress/types';
import SECTION_NAMES from 'constants/sectionNames';
import { useHouseholdMembersContext } from 'contexts/HouseholdMembersContext';
import usePrimaryFormDataQuery from 'hooks/queries/usePrimaryFormDataQuery';
import useSecondaryMembersFormDataQuery from 'hooks/queries/useSecondaryMembersFormDataQuery';

const PRIMARY_SECTIONS_TO_COMPLETE_FORM = [
  SECTION_NAMES.HOUSEHOLD,
  SECTION_NAMES.WAGES_AND_INCOME,
  SECTION_NAMES.ASSETS,

  SECTION_NAMES.PAYMENTS,
  SECTION_NAMES.IDENTIFYING_DETAILS,
  SECTION_NAMES.CREDIT_CRIMINAL_CHECK,

  SECTION_NAMES.SELF_IDENTIFICATION,
  SECTION_NAMES.APPLICATION_SUBMISSION,
];
const SECONDARY_SECTIONS_TO_COMPLETE_FORM = [
  SECTION_NAMES.WAGES_AND_INCOME,
  SECTION_NAMES.ASSETS,

  SECTION_NAMES.IDENTIFYING_DETAILS,
  SECTION_NAMES.CREDIT_CRIMINAL_CHECK,

  SECTION_NAMES.SELF_IDENTIFICATION,
  SECTION_NAMES.APPLICATION_SUBMISSION,
];
const UNINVITED_SECONDARY_USER_SECTIONS_TO_COMPLETE_FORM = [
  SECTION_NAMES.IDENTIFYING_DETAILS,
  SECTION_NAMES.CREDIT_CRIMINAL_CHECK,

  SECTION_NAMES.SELF_IDENTIFICATION,
  SECTION_NAMES.APPLICATION_SUBMISSION,
];

const isEverySectionComplete = (
  sections: string[],
  sectionsProgress: SectionsProgress,
) => {
  return sections.every((sectionName) => {
    return sectionsProgress[sectionName]?.isComplete === true;
  });
};

const useManageFormCompletion = ({
  sectionsProgress,
}: {
  sectionsProgress: SectionsProgress,
}) => {
  const {
    userProfile: { applicantId },
  } = useUserContext();
  const { isPrimaryUser, isSecondaryUser, isUnInvitedSecondaryUser } =
    useHouseholdMembersContext();
  const { data: primaryForm, isLoading: isPrimaryFormLoading } =
    usePrimaryFormDataQuery();
  const { data: secondaryForms, isLoading: isSecondaryFormsLoading } =
    useSecondaryMembersFormDataQuery({
      secondaryMembersApplicantIds:
        isSecondaryUser || isUnInvitedSecondaryUser ? [applicantId] : [],
    });
  const { mutate: savePrimaryFormMutate } = useSavePrimaryFormMutation();

  const { mutate: saveSecondaryFormMutate } = useSaveSecondaryFormMutation();

  // Use a ref to track if we've already called the mutation for this form completion
  const formCompletionMutationCalledRef = useRef(false);

  // Reset the ref when sectionsProgress changes
  useEffect(() => {
    formCompletionMutationCalledRef.current = false;
  }, [sectionsProgress]);

  useEffect(() => {
    if (isPrimaryFormLoading || isSecondaryFormsLoading || !applicantId?.length)
      return;

    // if form is complete
    if (isPrimaryUser && primaryForm?.isComplete === true) return;
    if (isSecondaryUser && secondaryForms[0]?.isComplete === true) return;

    // If we've already called the mutation for this form completion, don't call it again
    if (formCompletionMutationCalledRef.current) return;

    let shouldCompleteForm = false;

    if (isPrimaryUser) {
      shouldCompleteForm = isEverySectionComplete(
        PRIMARY_SECTIONS_TO_COMPLETE_FORM,
        sectionsProgress,
      );
    } else if (isSecondaryUser && !isUnInvitedSecondaryUser) {
      shouldCompleteForm = isEverySectionComplete(
        SECONDARY_SECTIONS_TO_COMPLETE_FORM,
        sectionsProgress,
      );
    } else if (isUnInvitedSecondaryUser) {
      shouldCompleteForm = isEverySectionComplete(
        UNINVITED_SECONDARY_USER_SECTIONS_TO_COMPLETE_FORM,
        sectionsProgress,
      );
    }

    // if form is not yet complete
    if (!shouldCompleteForm) return;

    // Set the primary/secondary forms as [complete: true]
    const payload = {
      applicantId,
      complete: true,
    };

    // Mark that we've called the mutation
    formCompletionMutationCalledRef.current = true;

    if (isPrimaryUser) {
      savePrimaryFormMutate(payload);
    } else if (isSecondaryUser || isUnInvitedSecondaryUser) {
      saveSecondaryFormMutate(payload);
    }
  }, [
    applicantId,
    isPrimaryUser,
    isSecondaryUser,
    isUnInvitedSecondaryUser,
    primaryForm,
    sectionsProgress,
    secondaryForms,
    savePrimaryFormMutate,
    saveSecondaryFormMutate,
    isPrimaryFormLoading,
    isSecondaryFormsLoading,
  ]);
};

export default useManageFormCompletion;
